import { useEffect, useState } from "react";

interface CategoryItem {
  name: string;
  images: string[];
}

const CATEGORY_API =
  "https://adinsinternational.com/websitebackend/category_api.php";

export default function Products() {
  const [categories, setCategories] = useState<CategoryItem[]>([]);
  const [loading, setLoading] = useState(true);

  const [selected, setSelected] = useState<CategoryItem | null>(null);
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await fetch(CATEGORY_API);
        const json = await res.json();

        if (json.status === "success") {
          /* merge duplicates by category_name */
          const map = new Map<string, string[]>();

          json.data.forEach((item: any) => {
            const name = item.category_name?.trim();
            const imgs = Array.isArray(item.image_urls) ? item.image_urls : [];
            if (!name || imgs.length === 0) return;

            if (!map.has(name)) map.set(name, []);
            map.get(name)!.push(...imgs);
          });

          const unique: CategoryItem[] = Array.from(map, ([name, imgs]) => {
            const dedup = Array.from(new Set(imgs));
            return { name, images: dedup };
          });

          setCategories(unique);
        } else {
          console.error("API error:", json);
        }
      } catch (err) {
        console.error("Failed to load categories:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    if (!selected) return;
    const total = selected.images.length;
    if (total <= 1) return;

    const id = setInterval(() => {
      setSlide((i) => (i + 1) % total);
    }, 3000);

    return () => clearInterval(id);
  }, [selected]);

  useEffect(() => {
    setSlide(0);
  }, [selected]);

  if (loading) {
    return (
      <div className="p-10 text-center text-xl font-medium text-gray-600">
        Loading&hellip;
      </div>
    );
  }

  // const handleBuyNow = (categoryName: string, image: string) => {
  //   const message = `Hi, I'm interested in the "${categoryName}" category product. Here is the image I saw:\n${image}`;
  //   window.open(
  //     `https://wa.me/919151816021?text=${encodeURIComponent(message)}`,
  //     "_blank"
  //   );
  // };
  const handleBuyNow = (categoryName: string, images: string[]) => {
    const message = `Hi, I'm interested in the "${categoryName}" category. Here are the product images:\n\n${images
      .map((img, i) => `${i + 1}. ${img}`)
      .join("\n")}`;

    const whatsappURL = `https://wa.me/919151816021?text=${encodeURIComponent(
      message
    )}`;
    window.open(whatsappURL, "_blank");
  };

  return (
    <>
      {/* Main grid */}
      <div className="container mx-auto px-6 py-12">
        <h1 className="text-4xl font-bold text-center text-gray-800 mb-8">
          Explore Our Products
        </h1>

        {categories.length === 0 ? (
          <p className="text-center text-gray-500">No categories found.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 mt-14">
            {categories.map(({ name, images }) => (
              <button
                key={name}
                onClick={() => setSelected({ name, images })}
                className="group relative bg-white rounded-xl shadow-lg hover:shadow-2xl transition-shadow duration-300 overflow-hidden text-left"
              >
                <div className="relative">
                  <img
                    src={images[0]}
                    alt={name}
                    className="w-full h-56 sm:h-64 object-cover transform group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black opacity-40 group-hover:opacity-30 transition-opacity duration-300" />
                </div>
                <div className="p-6">
                  <h3 className="text-2xl font-semibold text-gray-900 mb-2 group-hover:text-[#f98715] transition-colors duration-300">
                    {name}
                  </h3>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── Popover Modal ───────────────────────────────────────── */}
      {selected && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={() => setSelected(null)}
        >
          <div
            className="relative bg-white rounded-2xl shadow-2xl max-w-md w-[90%] sm:max-w-xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => setSelected(null)}
              className="absolute top-3 right-3 w-9 h-9 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center text-gray-600 text-2xl font-bold"
            >
              &times;
            </button>

            <div className="p-6 text-center">
              <h2 className="text-3xl font-bold mb-4  text-left">
                {selected.name}
              </h2>

              {/* Carousel */}
              <div className="relative">
                <img
                  src={selected.images[slide]}
                  alt={`${selected.name} ${slide + 1}`}
                  className="mx-auto rounded-xl max-h-[400px] object-contain"
                />

                {selected.images.length > 1 && (
                  <>
                    {/* Prev / Next controls */}
                    <button
                      className="absolute top-1/2 -translate-y-1/2 left-3 bg-white/70 hover:bg-white rounded-full w-9 h-9 flex items-center justify-center text-xl font-bold"
                      onClick={() =>
                        setSlide(
                          (i) =>
                            (i - 1 + selected.images.length) %
                            selected.images.length
                        )
                      }
                    >
                      ‹
                    </button>
                    <button
                      className="absolute top-1/2 -translate-y-1/2 right-3 bg-white/70 hover:bg-white rounded-full w-9 h-9 flex items-center justify-center text-xl font-bold"
                      onClick={() =>
                        setSlide((i) => (i + 1) % selected.images.length)
                      }
                    >
                      ›
                    </button>

                    {/* small dots */}
                    <div className="mt-4 flex justify-center gap-2">
                      {selected.images.map((_, idx) => (
                        <div
                          key={idx}
                          onClick={() => setSlide(idx)}
                          className={`w-2 h-2 rounded-full cursor-pointer ${
                            idx === slide
                              ? "bg-[#f98715]"
                              : "bg-gray-300 hover:bg-gray-400"
                          }`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
              <button
                className="mt-4 bg-[#f98715] text-white px-4 py-2 rounded shadow hover:bg-[#d87710]"
                onClick={() => handleBuyNow(selected.name, selected.images)}
              >
                Inquiry Now
              </button>
            </div>
            {/* <button
              className="mt-4 bg-[#f98715] text-white px-4 py-2 rounded shadow hover:bg-[#d87710]"
              onClick={() =>
                handleBuyNow(selected.name, selected.images[slide])
              }
            >
              Inquiry Now
            </button> */}
          </div>
        </div>
      )}
    </>
  );
}
